import React, { useState, useEffect, useRef } from 'react';
import { FaQuoteLeft, FaUserTie, FaChevronLeft, FaChevronRight, FaUsers } from 'react-icons/fa';
import ContactCardMen from '../Images/contactCardMen.png';

const testimonials = [
  {
    id: 1,
    name: 'Ethan Clarke',
    profession: 'IT Strategy & Modernization',
    image: ContactCardMen,
    text: 'DeepCyber Consulting helped us design a clear, actionable IT roadmap aligned with our growth objectives. Their IT Strategic Planning expertise and practical guidance on technology adoption helped us modernize legacy systems, streamline workflows, and optimize IT spending.',
  },
  {
    id: 2,
    name: 'Mia Schneider',
    profession: 'IT Helpdesk & Infrastructure Support',
    image: ContactCardMen,
    text: 'Our organization partnered with DeepCyber for IT Helpdesk Support and infrastructure management. Their quick response times, knowledgeable technicians, and proactive maintenance have kept our systems stable and our employees productive. The consistency and professionalism of their team make them a reliable long-term partner.',
  },
  {
    id: 3,
    name: 'Lucas Bennett',
    profession: 'Network & Endpoint Security Management',
    image: ContactCardMen,
    text: 'DeepCyber\'s cybersecurity team transformed our network and endpoint protection strategy. Their Firewall Management, Vulnerability Management, and Endpoint Protection services gave us continuous visibility into our environment and reduced our attack surface significantly. Through proactive monitoring, detailed reporting, and risk-based remediation, they\'ve helped us maintain compliance and prevent potential threats before they impact operations.',
  },
  {
    id: 4,
    name: 'Sofia Larsen',
    profession: 'Security Assessment & Awareness Training',
    image: ContactCardMen,
    text: 'We engaged DeepCyber for a comprehensive Security & Risk Assessment, followed by an End User Awareness Training program tailored to our industry. Their approach gave us actionable insights into our security maturity and empowered our staff to recognize threats early, significantly reducing incidents caused by human error.',
  },
  {
    id: 5,
    name: 'Raj Patel',
    profession: 'Custom Software Development',
    image: ContactCardMen,
    text: 'DeepCyber\'s Software Development team built a custom application that perfectly fit our operational needs. From planning to deployment, their agile approach ensured timely delivery and seamless integration with our existing systems. The result has been a major boost in efficiency and user satisfaction.',
  },
];

const TestimonialsSection = () => {
  // Triple the testimonials for seamless infinite loop
  const infiniteTestimonials = [...testimonials, ...testimonials, ...testimonials];
  const totalItems = testimonials.length; // 5
  
  // Start in the middle set (indices 5-9) to allow seamless looping
  // Start at index 6 to show testimonials 2, 3, 4 (which is index 1, 2, 3 in original array)
  const [currentIndex, setCurrentIndex] = useState(totalItems + 1); 
  const [itemsPerView, setItemsPerView] = useState(3);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const carouselRef = useRef(null);
  const sliderRef = useRef(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Update items per view based on screen size
  useEffect(() => {
    const updateItemsPerView = () => {
      if (window.innerWidth < 576) {
        setItemsPerView(1);
        // On mobile, start in middle set at index 6 to show testimonial 2
        setCurrentIndex(totalItems + 1);
      } else if (window.innerWidth < 992) {
        setItemsPerView(2);
        // On tablet, start in middle set at index 6 to show testimonials 2, 3
        setCurrentIndex(totalItems + 1);
      } else {
        setItemsPerView(3);
        // On desktop, start in middle set at index 6 to show testimonials 2, 3, 4
        setCurrentIndex(totalItems + 1);
      }
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, [totalItems]);

  // Handle seamless loop reset
  useEffect(() => {
    if (!sliderRef.current) return;

    const handleTransitionEnd = () => {
      // If we're at or past the end of the middle set (index 10+), 
      // jump to equivalent position in the middle set (index 5-9)
      if (currentIndex >= totalItems * 2) {
        setIsTransitioning(false);
        // Reset to equivalent position in middle set
        setCurrentIndex(totalItems + (currentIndex % totalItems));
        // Force reflow and re-enable transition
        setTimeout(() => {
          setIsTransitioning(true);
        }, 50);
      }
      // If we're before the start of the middle set (index < 5),
      // jump to equivalent position at the end of middle set
      else if (currentIndex < totalItems) {
        setIsTransitioning(false);
        // Reset to equivalent position in middle set
        setCurrentIndex(totalItems + (currentIndex % totalItems));
        setTimeout(() => {
          setIsTransitioning(true);
        }, 50);
      }
    };

    const slider = sliderRef.current;
    slider.addEventListener('transitionend', handleTransitionEnd);
    return () => slider.removeEventListener('transitionend', handleTransitionEnd);
  }, [currentIndex, totalItems]);

  // Handle touch start (mobile)
  const onTouchStart = (e) => {
    setIsPaused(true);
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  // Handle touch move (mobile)
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  // Handle touch end (mobile)
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrev();
    }
  };

  // Handle mouse down (desktop drag)
  const onMouseDown = (e) => {
    setIsPaused(true);
    setIsDragging(true);
    setDragStart(e.clientX);
    setDragOffset(0);
  };


  // Handle next slide - with seamless infinite loop
  const handleNext = () => {
    setIsPaused(true);
    setCurrentIndex((prev) => prev + 1);
    // Resume auto-play after 5 seconds
    setTimeout(() => setIsPaused(false), 5000);
  };

  // Handle previous slide - with seamless infinite loop
  const handlePrev = () => {
    setIsPaused(true);
    setCurrentIndex((prev) => prev - 1);
    // Resume auto-play after 5 seconds
    setTimeout(() => setIsPaused(false), 5000);
  };

  // Get the visual position for dots (0-4)
  const getVisualPosition = (index) => {
    return index % totalItems;
  };

  // Global mouse move listener for drag
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e) => {
        if (dragStart !== null) {
          const offset = e.clientX - dragStart;
          setDragOffset(offset);
        }
      };

      const handleGlobalMouseUp = () => {
        if (Math.abs(dragOffset) > minSwipeDistance) {
          if (dragOffset > 0) {
            setCurrentIndex((prev) => prev - 1);
            setIsPaused(true);
            setTimeout(() => setIsPaused(false), 5000);
          } else if (dragOffset < 0) {
            setCurrentIndex((prev) => prev + 1);
            setIsPaused(true);
            setTimeout(() => setIsPaused(false), 5000);
          }
        }
        
        setIsDragging(false);
        setDragStart(null);
        setDragOffset(0);
      };

      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleGlobalMouseMove);
        window.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStart, dragOffset]);

  // Handle mouse leave (desktop drag)
  const onMouseLeave = () => {
    setIsPaused(false);
    if (isDragging) {
      setIsDragging(false);
      setDragStart(null);
      setDragOffset(0);
    }
  };

  // Auto-play functionality - continuous rotation
  useEffect(() => {
    if (isPaused || isDragging) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => prev + 1);
    }, 3000); // Change slide every 3 seconds for smoother rotation

    return () => clearInterval(interval);
  }, [isPaused, isDragging]);

  return (
    <div className="py-2" id="review">
      <div className="container py-3">
        <div className="text-center mb-5">
          <p className="d-inline-block border rounded fw-semibold py-1 px-3 text-navyblue">
            <FaUsers className="me-2" /> Testimonials
          </p>
          <h1 className="mb-0">What Say Our Clients!</h1>
        </div>

        <div className="position-relative">
          {/* Carousel Container */}
          <div
            ref={carouselRef}
            className="testimonials-carousel py-4"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onMouseDown={onMouseDown}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={onMouseLeave}
            style={{
              overflow: 'hidden',
              position: 'relative',
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none',
            }}
          >
            <div
              ref={sliderRef}
              className="d-flex testimonials-slider"
              style={{
                transform: `translateX(calc(-${currentIndex * (100 / itemsPerView)}% + ${dragOffset}px))`,
                transition: isDragging || !isTransitioning ? 'none' : 'transform 0.5s ease-in-out',
              }}
            >
              {/* Triple testimonials for seamless infinite loop */}
              {infiniteTestimonials.map(({ id, name, profession, image, text }, index) => (
                <div
                  key={`${id}-${index}`}
                  className="testimonial-slide"
                  style={{
                    flex: `0 0 ${100 / itemsPerView}%`,
                    maxWidth: `${100 / itemsPerView}%`,
                    padding: '0 12px',
                  }}
                >
                  <div className="testimonial-item rounded p-4 shadow-sm bg-white h-100">
                    <div className="d-flex flex-column align-items-center text-center mb-3">
                      <div>
                         <FaUserTie className="m-2 text-navyblue"style={{"height":"85px",width:"85px"}} />
                      </div>
                      <h5 className="mb-1 fw-bold">{name}</h5>
                      <p className="mb-2 text-muted small">
                        {profession}
                      </p>
                    </div>
                    <p className="text-navyblue mb-0" style={{ lineHeight: '1.6' }}>
                      <FaQuoteLeft className="me-2 text-navyblue" />
                      {text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows - Always visible for infinite loop */}
          <button
            className="testimonial-nav-btn testimonial-nav-prev"
            onClick={handlePrev}
            aria-label="Previous testimonials"
          >
            <FaChevronLeft />
          </button>

          <button
            className="testimonial-nav-btn testimonial-nav-next"
            onClick={handleNext}
            aria-label="Next testimonials"
          >
            <FaChevronRight />
          </button>

          {/* Dots Indicator */}
          <div className="d-flex justify-content-center mt-4 gap-2">
            {Array.from({ length: totalItems }).map((_, index) => {
              const visualPos = getVisualPosition(currentIndex);
              return (
                <button
                  key={index}
                  className={`testimonial-dot ${index === visualPos ? 'active' : ''}`}
                  onClick={() => {
                    setIsPaused(true);
                    // Calculate the target index in the middle set
                    const targetIndex = totalItems + index;
                    setCurrentIndex(targetIndex);
                    // Resume auto-play after 5 seconds
                    setTimeout(() => setIsPaused(false), 5000);
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialsSection;
